import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import ProtectedRoute from "./Components/ProtectedRoute/ProtectedRoute";

// ============================================================================
// Lazy-loaded Route Components
// ============================================================================

// Authentication
const LoginPage = lazy(
  () => import("./Components/MslaLoginComponent/MslaLoginComponent")
);
const SecondaryLoginPage = lazy(() => import("./Components/Login/Login"));

// Main Application
const AuditLogPage = lazy(
  () => import("./Containers/AuditLogComponent/UserAuditLog")
);
const DoorSchedulePage = lazy(
  () => import("./Containers/DoorScheduleContainer")
);
const ProjectDetailsPage = lazy(
  () =>
    import("./Containers/ProjectDetailsContainer/ProjectDetailsContainer.jsx")
);
const FileListingPage = lazy(
  () =>
    import("./Components/ProccessedFileComponent/ProcessedFileComponent.jsx")
);

// Maintenance (conditionally loaded)
const MaintenancePage = lazy(
  () => import("./Containers/MaintanancePageComponent/MaintenancePage.jsx")
);

// ============================================================================
// Loading Fallback Component
// ============================================================================

const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      flexDirection: "column",
      gap: "1rem",
    }}
  >
    <ProgressSpinner
      style={{ width: "50px", height: "50px" }}
      strokeWidth="4"
      animationDuration="1s"
    />
    <p style={{ color: "#6c757d", fontSize: "0.875rem" }}>Loading...</p>
  </div>
);

// ============================================================================
// Routes Configuration
// ============================================================================

const AppRoutes = () => {
  // Environment-based feature flag for maintenance mode
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === "true";

  if (isMaintenanceMode) {
    return (
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="*" element={<MaintenancePage />} />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Authentication Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/secondary-login" element={<SecondaryLoginPage />} />

          {/* Application Routes */}
          <Route path="/audit-log" element={<AuditLogPage />} />
          <Route path="/doorSchedule" element={<DoorSchedulePage />} />

          {/* Protected subroutes - require selectedProject in localStorage */}
          <Route
            path="/doorSchedule/:pId"
            element={
              <ProtectedRoute requiredData={["selectedProject"]}>
                <ProjectDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doorSchedule/:projectId/:selectedFile"
            element={
              <ProtectedRoute requiredData={["selectedProject"]}>
                <FileListingPage />
              </ProtectedRoute>
            }
          />

          {/* Default Redirects */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
